import React from "react";
import {
  Box,
  ButtonBase,
  Collapse,
  Grid,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";

const defaultSectionSx = {
  p: { xs: 2, md: 3 },
  borderRadius: 2,
  borderColor: "divider",
};

const emptyValue = "-";

const pickFirst = (...values) =>
  values.find(
    (value) => value !== undefined && value !== null && value !== "",
  );

const formatUserName = (user) => {
  if (!user) return "";
  if (typeof user === "string") return user;

  return (
    [user.firstname || user.firstName, user.name || user.lastName]
      .filter(Boolean)
      .join(" ") ||
    user.username ||
    user.email ||
    ""
  );
};

const formatDate = (dateValue) => {
  if (!dateValue) return "";

  const date = dayjs(dateValue);
  return date.isValid() ? date.format("DD/MM/YYYY") : "";
};

const toDateTimeInputValue = (dateValue) => {
  if (!dateValue) return "";

  const date = dayjs(dateValue);
  return date.isValid() ? date.format("YYYY-MM-DDTHH:mm") : "";
};

const fromDateTimeInputValue = (value) => {
  if (!value) return null;

  const date = dayjs(value);
  return date.isValid() ? date.toISOString() : null;
};

const normalizeTrackingValue = ({ user, date }) => ({
  name: formatUserName(user),
  email: typeof user === "object" ? user?.email || "" : "",
  date: date || "",
});

const ReadOnlyField = ({ label, value }) => (
  <TextField
    fullWidth
    label={label}
    value={value || emptyValue}
    InputProps={{ readOnly: true }}
    InputLabelProps={{ shrink: true }}
  />
);

const TrackingGroup = ({ title, user, date, editable = false, onChange }) => {
  const { t } = useTranslation();
  const value = normalizeTrackingValue({ user, date });
  const updateValue = (field, nextValue) => {
    if (!onChange) return;

    onChange({
      ...value,
      [field]: nextValue,
    });
  };

  return (
    <Box
      sx={{
        p: 2,
        borderRadius: 1,
        bgcolor: "grey.50",
        border: "1px solid",
        borderColor: "divider",
      }}>
      <Typography sx={{ fontWeight: 700, mb: 2 }}>{title}</Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} md={4}>
          {editable ? (
            <TextField
              fullWidth
              label={t("name")}
              value={value.name}
              onChange={(event) => updateValue("name", event.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          ) : (
            <ReadOnlyField label={t("name")} value={value.name} />
          )}
        </Grid>
        <Grid item xs={12} md={4}>
          {editable ? (
            <TextField
              fullWidth
              label={t("email")}
              value={value.email}
              onChange={(event) => updateValue("email", event.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          ) : (
            <ReadOnlyField label={t("email")} value={value.email} />
          )}
        </Grid>
        <Grid item xs={12} md={4}>
          {editable ? (
            <TextField
              fullWidth
              label={t("date")}
              type="datetime-local"
              value={toDateTimeInputValue(value.date)}
              onChange={(event) =>
                updateValue("date", fromDateTimeInputValue(event.target.value))
              }
              InputLabelProps={{ shrink: true }}
            />
          ) : (
            <ReadOnlyField label={t("date")} value={formatDate(value.date)} />
          )}
        </Grid>
      </Grid>
    </Box>
  );
};

const SampleShipmentAudit = ({
  sampleShipment = {},
  draftShipment = {},
  fallbackRequester = null,
  requestDate = null,
  sectionSx = defaultSectionSx,
  visibleSteps = ["request", "confirm", "receive"],
  editable = false,
  trackingValues = {},
  onTrackingChange = {},
}) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = React.useState(false);
  const contentId = React.useId();
  const requester = pickFirst(
    sampleShipment.createdByUser,
    sampleShipment.user,
    sampleShipment.User,
    draftShipment.user,
    fallbackRequester,
  );
  const confirmer = pickFirst(
    sampleShipment.confirmedByUser,
    sampleShipment.confirmUser,
    sampleShipment.confirmedUser,
  );
  const receiver = pickFirst(
    sampleShipment.receiveByUser,
    sampleShipment.receivedByUser,
    sampleShipment.reception?.receiveByUser,
  );
  const requestedAt = pickFirst(
    sampleShipment.createdAt,
    sampleShipment.sendingDate,
    draftShipment.createdAt,
    requestDate,
  );
  const confirmedAt = sampleShipment.confirmedDatetime;
  const receivedAt = pickFirst(
    sampleShipment.receiveDatetime,
    sampleShipment.reception?.receiveDatetime,
  );
  const groups = [
    {
      step: "request",
      title: t("personRequesting", "Person Requesting"),
      user: trackingValues.request?.user || trackingValues.request || requester,
      date: trackingValues.request?.date || requestedAt,
      onChange: onTrackingChange.request,
    },
    {
      step: "confirm",
      title: t("personConfirming", "Person Confirming"),
      user: trackingValues.confirm?.user || trackingValues.confirm || confirmer,
      date: trackingValues.confirm?.date || confirmedAt,
      onChange: onTrackingChange.confirm,
    },
    {
      step: "receive",
      title: t("personReceiving", "Person Receiving"),
      user: trackingValues.receive?.user || trackingValues.receive || receiver,
      date: trackingValues.receive?.date || receivedAt,
      onChange: onTrackingChange.receive,
    },
  ].filter(({ step }) => visibleSteps.includes(step));

  return (
    <Paper variant="outlined" sx={sectionSx}>
      <ButtonBase
        onClick={() => setExpanded((previous) => !previous)}
        aria-expanded={expanded}
        aria-controls={contentId}
        sx={{
          display: "flex",
          justifyContent: "space-between",
          width: "100%",
          textAlign: "left",
          borderRadius: 1,
          "&.Mui-focusVisible": {
            outline: "2px solid",
            outlineColor: "primary.main",
            outlineOffset: 4,
          },
        }}>
        <Typography
          variant="h6"
          sx={{ color: "primary.main", fontWeight: 700, lineHeight: 1.25 }}>
          {t("auditTrail", "Audit Trail")}
        </Typography>
        <ExpandMoreIcon
          color="primary"
          sx={{ transform: expanded ? "rotate(180deg)" : "rotate(0deg)" }}
        />
      </ButtonBase>
      <Collapse in={expanded} id={contentId}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 2.5 }}>
          {groups.map((group) => (
            <TrackingGroup
              key={group.step}
              title={group.title}
              user={group.user}
              date={group.date}
              editable={editable && Boolean(group.onChange)}
              onChange={group.onChange}
            />
          ))}
        </Box>
      </Collapse>
    </Paper>
  );
};

export default SampleShipmentAudit;
